// src/components/forms/RappelForm.tsx
//
// Formulaire "Rappelez-moi" du hero de la page /entreprise/recrutement-dirigeants.
// Champs : Prénom, Nom, Société, Téléphone + consentement commercial (RGPD).
// Soumet à /.netlify/functions/rappel qui notifie candidature@recruteurs.io.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';

interface FormState {
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  consent: boolean;
  website: string; // honeypot
}

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  company: '',
  phone: '',
  consent: false,
  website: '',
};

const inputClass =
  'w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm text-[#30475e] placeholder:text-neutral-400 focus:border-[#d03484] focus:outline-none focus:ring-2 focus:ring-pink-100 transition';

export default function RappelForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.firstName.trim() || !form.lastName.trim() || !form.company.trim() || !form.phone.trim()) {
      setError('Prénom, nom, société et téléphone sont requis.');
      return;
    }
    if (!/^[0-9 +().\-]{6,25}$/.test(form.phone.trim())) {
      setError('Numéro de téléphone invalide.');
      return;
    }
    if (!form.consent) {
      setError('Merci de cocher la case de consentement.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/.netlify/functions/rappel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          company: form.company.trim(),
          phone: form.phone.trim(),
          consent: form.consent,
          website: form.website,
        }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? "L'envoi a échoué. Réessayez.");
        setStatus('error');
      }
    } catch {
      setError('Erreur réseau. Réessayez.');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  }

  if (status === 'success') {
    return (
      <div className="rounded-3xl border border-neutral-200 bg-white p-8 text-center shadow-xl">
        <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-500" />
        <h3 className="text-xl font-bold text-[#30475e]">Votre demande est bien reçue.</h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-600">
          Je vous rappelle très rapidement, en toute confidentialité, pour
          cadrer votre besoin. À très vite.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-xl md:p-7">
      <div className="mb-4 flex items-center gap-2">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#d03484] to-[#d47254]">
          <Phone className="h-4 w-4 text-white" />
        </span>
        <div>
          <h3 className="text-base font-bold leading-tight text-[#30475e]">
            Être rappelé·e
          </h3>
          <p className="text-xs text-neutral-500">Échange confidentiel, sans engagement</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3" noValidate>
        {/* Honeypot */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => set('website', e.target.value)}
          className="hidden"
          aria-hidden="true"
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
              Prénom <span className="text-[#d03484]">*</span>
            </label>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              placeholder="Jean"
              autoComplete="given-name"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
              Nom <span className="text-[#d03484]">*</span>
            </label>
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              placeholder="Dupont"
              autoComplete="family-name"
              required
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
            Société <span className="text-[#d03484]">*</span>
          </label>
          <input
            type="text"
            value={form.company}
            onChange={(e) => set('company', e.target.value)}
            placeholder="Nom de votre entreprise"
            autoComplete="organization"
            required
            className={inputClass}
          />
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
            Téléphone <span className="text-[#d03484]">*</span>
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="06 12 34 56 78"
            autoComplete="tel"
            required
            className={inputClass}
          />
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 pt-1">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) => set('consent', e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-[#d03484] focus:ring-[#d03484]"
          />
          <span className="text-[11px] leading-snug text-neutral-500">
            J’accepte que mes données soient utilisées à des fins commerciales par
            recruteurs.io.{' '}
            <Link
              to="/legal/confidentialite"
              className="underline hover:text-[#d03484]"
            >
              En savoir plus
            </Link>
            .
          </span>
        </label>

        {error && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-pink-100 transition hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Phone className="h-4 w-4" />
          )}
          Rappelez-moi
        </button>

        <p className="flex items-center justify-center gap-1.5 text-[11px] text-neutral-400">
          <ShieldCheck className="h-3 w-3" />
          Données traitées en France · confidentiel · aucun engagement
        </p>
      </form>
    </div>
  );
}
